import { useState, useCallback, useEffect, useContext } from "react";
import { useDropzone } from "react-dropzone";
import { FiUploadCloud, FiXCircle } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { SketchPicker } from "react-color";
import { getTextColor } from "utils/color";
import { getSettingsPanel, updateSettings } from "api/settings";
import { ClientContext } from "ClientProvider";
import DivLoader from "components/divloader/DivLoader";

/* ─────────────────────────────────────
   Color Picker sub-component
───────────────────────────────────── */
const ColorPicker = ({
  label, color, onChange, required,
}: {
  label: string; color: string;
  onChange: (color: string) => void;
  required?: boolean;
}) => {
  const [showPicker, setShowPicker] = useState(false);

  const handleColorChange = (c: any) => {
    onChange(`rgba(${c.rgb.r}, ${c.rgb.g}, ${c.rgb.b}, ${c.rgb.a})`);
  };

  const hasColor = !!color;

  return (
    <div className="st-color-field">
      <label className="st-field-label">
        {label}{required && <span className="st-req">*</span>}
      </label>
      <div className="st-color-preview-wrap">
        <button
          className="st-color-preview"
          style={{ background: hasColor ? color : "#f8fafc" }}
          onClick={() => setShowPicker((p) => !p)}
          aria-label={`Pick ${label}`}
        >
          {hasColor ? (
            <span className="st-color-hex" style={{ color: getTextColor(color) }}>
              {color.startsWith("rgba") ? color : color}
            </span>
          ) : (
            <span className="st-color-placeholder">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/>
                <circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/>
                <circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/>
                <circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/>
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
              </svg>
              Select Color
            </span>
          )}
        </button>
        {showPicker && (
          <div className="st-picker-popup">
            <div className="st-picker-backdrop" onClick={() => setShowPicker(false)} />
            <div className="st-picker-inner">
              <SketchPicker color={color || "#ffffff"} onChange={handleColorChange} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────
   File Dropzone sub-component
───────────────────────────────────── */
const FileDropzone = ({
  label, sublabel, onFileSelect, initialPreview, icon,
}: {
  label: string; sublabel?: string;
  onFileSelect: (file: string | null) => void;
  initialPreview: any;
  icon?: React.ReactNode;
}) => {
  const [preview, setPreview] = useState<string | null>(initialPreview);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => { setPreview(initialPreview); }, [initialPreview]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setIsUploading(true);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      const fileUrl = reader.result as string;
      setPreview(fileUrl);
      onFileSelect(fileUrl);
      setIsUploading(false);
    };
  }, [onFileSelect]);

  const removeImage = () => { setPreview(null); onFileSelect(null); };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: (rj) => {
      const code = rj[0]?.errors[0]?.code;
      if (code === "file-too-large") toast.error("File must be less than 2MB");
      else if (code === "file-invalid-type") toast.error("Only PNG and SVG files are allowed");
      else toast.error("Upload failed. Please try again.");
    },
    accept: { "image/png": [], "image/svg": [], "image/svg+xml": [] },
    maxSize: 2 * 1024 * 1024,
    multiple: false,
  });

  return (
    <div className={`st-dropzone${isDragActive ? " st-dropzone--drag" : ""}${preview ? " st-dropzone--has-file" : ""}`} {...getRootProps()}>
      <input {...getInputProps()} />
      {isUploading ? (
        <div className="st-dropzone-uploading">
          <DivLoader className="h-7 w-7 border-blue-500" />
          <p className="st-dropzone-uploading-txt">Processing…</p>
        </div>
      ) : preview ? (
        <div className="st-dropzone-preview" onClick={(e) => e.stopPropagation()}>
          <img src={preview} alt="Preview" className="st-dropzone-img" />
          <button
            className="st-dropzone-remove"
            onClick={(e) => { e.stopPropagation(); removeImage(); }}
            aria-label="Remove"
          >
            <FiXCircle size={14} />
          </button>
        </div>
      ) : (
        <div className="st-dropzone-empty">
          <div className="st-dropzone-upload-icon">{icon ?? <FiUploadCloud size={22} />}</div>
          <p className="st-dropzone-label">{label}</p>
          {sublabel && <p className="st-dropzone-sub">{sublabel}</p>}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────
   Main Settings page
───────────────────────────────────── */
const Settings = () => {
  const [initialLoading, setInitialLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginIcon, setLoginIcon] = useState<string | null>(null);
  const [portalIcon, setPortalIcon] = useState<string | null>(null);
  const [favicon, setFavicon] = useState<string | null>(null);
  const [activeColor, setActiveColor] = useState("#422afb");
  const [hoverColor, setHoverColor] = useState("#283547");
  const [fromColor, setFromColor] = useState("#271996");
  const [toColor, setToColor] = useState("#422afb");
  const { singleRoleData } = useContext(ClientContext);

  const rgbaToHex = (rgba: string) => {
    const m = rgba.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]*)\)$/);
    if (!m) return rgba;
    let [, r, g, b, a]: any = m;
    r = parseInt(r).toString(16).padStart(2, "0");
    g = parseInt(g).toString(16).padStart(2, "0");
    b = parseInt(b).toString(16).padStart(2, "0");
    if (!a || a === "1") return `#${r}${g}${b}`;
    a = Math.round(parseFloat(a) * 255).toString(16).padStart(2, "0");
    return `#${r}${g}${b}${a}`;
  };

  const fetchSettings = () => {
    setInitialLoading(true);
    getSettingsPanel(true)
      .then((data) => {
        setLoginIcon(data.login_icon);
        setPortalIcon(data.portal_icon);
        setFavicon(data.favicon);
        setActiveColor(data.button_active_color ?? "");
        setHoverColor(data.button_hover_color ?? "");
        setFromColor(data.gradient_from_color ?? "");
        setToColor(data.gradient_to_color ?? "");
      })
      .catch(() => {})
      .finally(() => { setInitialLoading(false); setIsLoading(false); });
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSubmit = async () => {
    if (!loginIcon || !portalIcon || !favicon || !activeColor || !hoverColor) {
      toast.error("Please fill all required fields.");
      return;
    }
    setIsLoading(true);
    try {
      const payload = { login_icon: loginIcon, portal_icon: portalIcon, favicon, button_active_color: activeColor, button_hover_color: hoverColor, gradient_from_color: fromColor, gradient_to_color: toColor };
      const data = await updateSettings(payload);

      const styles = `:root { --indigo-500:${hoverColor}!important; --indigo-600:${activeColor}!important; --indigo-700:${hoverColor}!important; --brand-500:${activeColor}!important; --brand-600:${hoverColor}!important; }`;
      let styleTag = document.getElementById("dynamic-theme") as HTMLStyleElement;
      if (!styleTag) { styleTag = document.createElement("style"); styleTag.id = "dynamic-theme"; document.head.appendChild(styleTag); }
      styleTag.innerHTML = styles;

      const link: any = document.querySelector("link[rel~='icon']") || document.createElement("link");
      link.rel = "icon"; link.href = favicon; document.head.appendChild(link);

      const img = document.querySelector("img#portalIcon") as HTMLImageElement;
      if (img) img.src = portalIcon;

      toast.success(data.message);
      fetchSettings();
    } catch (err: any) {
      toast.error(err?.message || "Failed to update settings");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="st-root">
      {/* Page header */}
      <div className="st-page-header">
        <div className="st-title-block">
          <div className="st-page-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </div>
          <div>
            <h1 className="st-heading">Settings</h1>
            <p className="st-sub">Customize portal logos</p>
            {/* <p className="st-sub">Customize portal logos, brand colors and gradients</p> */}
          </div>
        </div>
      </div>

      {/* Main card */}
      <div className="st-card">
        {initialLoading ? (
          <div className="st-initial-loading">
            <div className="st-init-spinner" />
            <p className="st-init-txt">Loading settings…</p>
          </div>
        ) : (
          <>
            {/* ── Section: Logos ── */}
            <div className="st-section">
              <div className="st-section-header">
                <div className="st-section-icon st-section-icon--blue">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                </div>
                <div>
                  <h2 className="st-section-title">Brand Logos</h2>
                  <p className="st-section-desc">PNG or SVG, max 2MB each</p>
                </div>
              </div>

              <div className="st-logos-grid">
                {[
                  { label: "Login Icon", sublabel: "Shown on sign-in page", state: loginIcon, setter: setLoginIcon,
                    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg> },
                  { label: "Portal Icon", sublabel: "Shown in sidebar", state: portalIcon, setter: setPortalIcon,
                    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg> },
                  { label: "Favicon", sublabel: "Browser tab icon", state: favicon, setter: setFavicon,
                    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg> },
                ].map((item) => (
                  <div key={item.label} className="st-logo-slot">
                    <div className="st-logo-slot-label">
                      {item.label} <span className="st-req">*</span>
                    </div>
                    <p className="st-logo-slot-sub">{item.sublabel}</p>
                    <FileDropzone
                      label={`Drop ${item.label}`}
                      sublabel="PNG or SVG"
                      onFileSelect={item.setter}
                      initialPreview={item.state}
                      icon={item.icon}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* <div className="st-section-divider">
              <span className="st-section-divider-label">Colors</span>
              <div className="st-section-divider-line" />
            </div> */}

            {/* ── Section: Button Colors ── */}
            {/* <div className="st-colors-grid">
              <div className="st-section st-section--color">
                <div className="st-section-header">
                  <div className="st-section-icon st-section-icon--amber">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                  </div>
                  <div>
                    <h2 className="st-section-title">Button Colors</h2>
                    <p className="st-section-desc">Applied to primary action buttons</p>
                  </div>
                </div>

                <div className="st-pickers-row">
                  <ColorPicker label="Active Color" color={activeColor} onChange={(e) => setActiveColor(rgbaToHex(e))} required />
                  <ColorPicker label="Hover Color" color={hoverColor} onChange={(e) => setHoverColor(rgbaToHex(e))} required />
                </div>

                {activeColor && hoverColor && (
                  <div className="st-preview-section">
                    <p className="st-preview-label">Preview</p>
                    <button
                      className="st-preview-btn"
                      style={{ background: activeColor, color: getTextColor(activeColor) }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = hoverColor; e.currentTarget.style.color = getTextColor(hoverColor); }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = activeColor; e.currentTarget.style.color = getTextColor(activeColor); }}
                    >
                      Preview Button
                    </button>
                  </div>
                )}
              </div>
              <div className="st-section st-section--color">
                <div className="st-section-header">
                  <div className="st-section-icon st-section-icon--purple">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#7c3aed"/><stop offset="100%" stopColor="#2563eb"/></linearGradient></defs>
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                  </div>
                  <div>
                    <h2 className="st-section-title">Gradient Colors</h2>
                    <p className="st-section-desc">Used for sidebar and accent backgrounds</p>
                  </div>
                </div>

                <div className="st-pickers-row">
                  <ColorPicker label="From Color" color={fromColor} onChange={(e) => setFromColor(rgbaToHex(e))} />
                  <ColorPicker label="To Color" color={toColor} onChange={(e) => setToColor(rgbaToHex(e))} />
                </div>

                {fromColor && toColor && (
                  <div className="st-preview-section">
                    <p className="st-preview-label">Preview</p>
                    <div className="st-gradient-preview" style={{ background: `linear-gradient(130deg, ${fromColor} 0%, ${toColor} 100%)` }} />
                  </div>
                )}
              </div>
            </div> */}

            {/* ── Submit ── */}
            {singleRoleData?.[0]?.settings?.value?.update_settings && (
              <div className="st-footer">
                <div className="st-footer-hint">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Changes are applied immediately after saving
                </div>
                <button className="st-save-btn" onClick={handleSubmit} disabled={isLoading}>
                  {isLoading ? <span className="st-save-spinner" /> : (
                    <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Save Settings</>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .st-root { font-family:'Plus Jakarta Sans',sans-serif; display:flex; flex-direction:column; gap:20px; }

        /* Page header */
        .st-page-header { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:16px; }
        .st-title-block { display:flex; align-items:center; gap:14px; }
        .st-page-icon { width:44px; height:44px; border-radius:12px; background:linear-gradient(135deg,#f8fafc,#f1f5f9); border:1.5px solid #e2e8f0; display:flex; align-items:center; justify-content:center; color:#64748b; flex-shrink:0; }
        .st-heading { font-size:20px; font-weight:800; color:#0f172a; letter-spacing:-0.4px; margin-bottom:2px; line-height:1.2; }
        .st-sub { font-size:13px; color:#94a3b8; font-weight:500; }

        /* Card */
        .st-card { background:#fff; border:1px solid #e2e8f0; border-radius:18px; overflow:hidden; box-shadow:0 2px 8px rgba(15,23,42,0.06); }

        /* Initial loading */
        .st-initial-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:72px; gap:14px; }
        .st-init-spinner { width:32px; height:32px; border:3px solid #e2e8f0; border-top-color:#2563eb; border-radius:50%; animation:stSpin .7s linear infinite; }
        @keyframes stSpin { to{transform:rotate(360deg)} }
        .st-init-txt { font-size:13.5px; color:#94a3b8; font-weight:500; }

        /* Section */
        .st-section { padding:28px; display:flex; flex-direction:column; gap:20px; }
        .st-section--color { border:1.5px solid #e2e8f0; border-radius:14px; }
        .st-section-header { display:flex; align-items:flex-start; gap:12px; }
        .st-section-icon { width:38px; height:38px; border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .st-section-icon--blue { background:linear-gradient(135deg,#eff6ff,#dbeafe); border:1.5px solid #bfdbfe; color:#2563eb; }
        .st-section-icon--amber { background:linear-gradient(135deg,#fffbeb,#fef3c7); border:1.5px solid #fde68a; color:#d97706; }
        .st-section-icon--purple { background:linear-gradient(135deg,#fdf4ff,#fae8ff); border:1.5px solid #e9d5ff; color:#7c3aed; }
        .st-section-title { font-size:14px; font-weight:800; color:#0f172a; letter-spacing:-0.2px; margin-bottom:2px; line-height:1.2; }
        .st-section-desc { font-size:12px; color:#94a3b8; font-weight:500; }

        .st-section-divider { display:flex; align-items:center; gap:12px; padding:0 28px; }
        .st-section-divider-label { font-size:9.5px; font-weight:800; color:#b8c4d8; text-transform:uppercase; letter-spacing:1px; white-space:nowrap; }
        .st-section-divider-line { flex:1; height:1px; background:linear-gradient(90deg,#e4e9f4,transparent); }

        .st-colors-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; padding:10px 28px 28px; }

        /* Logos */
        .st-logos-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; }
        .st-logo-slot { display:flex; flex-direction:column; gap:4px; }
        .st-logo-slot-label { font-size:12.5px; font-weight:700; color:#374151; }
        .st-logo-slot-sub { font-size:11.5px; color:#94a3b8; font-weight:500; margin-bottom:2px; }

        /* Dropzone */
        .st-dropzone {
          display:flex; flex-direction:column; align-items:center; justify-content:center;
          border:1.5px dashed #cbd5e1; border-radius:13px; cursor:pointer;
          background:#f8fafc; min-height:120px;
          transition:border-color .2s, background .2s;
          position:relative; overflow:hidden;
        }
        .st-dropzone:hover { border-color:#2563eb; background:#eff6ff; }
        .st-dropzone--drag { border-color:#2563eb; background:#eff6ff; }
        .st-dropzone--has-file { border-style:solid; border-color:#bfdbfe; background:#f0f9ff; }
        .st-dropzone-empty { display:flex; flex-direction:column; align-items:center; gap:8px; padding:20px 16px; }
        .st-dropzone-upload-icon { width:42px; height:42px; border-radius:11px; background:#fff; border:1.5px solid #e2e8f0; display:flex; align-items:center; justify-content:center; color:#2563eb; }
        .st-dropzone-label { font-size:12.5px; font-weight:600; color:#374151; text-align:center; }
        .st-dropzone-sub { font-size:11px; color:#94a3b8; font-weight:500; }
        .st-dropzone-uploading { display:flex; flex-direction:column; align-items:center; gap:8px; padding:24px; }
        .st-dropzone-uploading-txt { font-size:12px; color:#94a3b8; font-weight:500; }
        .st-dropzone-preview { position:relative; padding:12px; }
        .st-dropzone-img { width:80px; height:80px; border-radius:10px; object-fit:contain; border:1.5px solid #e2e8f0; }
        .st-dropzone-remove { position:absolute; top:6px; right:6px; width:22px; height:22px; border-radius:50%; background:#ef4444; border:none; color:#fff; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:background .15s; }
        .st-dropzone-remove:hover { background:#dc2626; }

        /* Color picker */
        .st-color-field { display:flex; flex-direction:column; gap:6px; }
        .st-field-label { font-size:12.5px; font-weight:700; color:#374151; }
        .st-req { color:#dc2626; }
        .st-color-preview-wrap { position:relative; }
        .st-color-preview {
          width:100%; height:44px; border-radius:11px; border:1.5px solid #e2e8f0;
          cursor:pointer; display:flex; align-items:center; justify-content:center;
          transition:border-color .15s, box-shadow .15s; padding:0 12px;
          font-family:'Plus Jakarta Sans',sans-serif;
        }
        .st-color-preview:hover { border-color:#2563eb; box-shadow:0 0 0 3px rgba(37,99,235,.08); }
        .st-color-hex { font-size:11.5px; font-weight:700; letter-spacing:0.3px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:100%; }
        .st-color-placeholder { display:flex; align-items:center; gap:6px; font-size:12.5px; font-weight:600; color:#94a3b8; }
        .st-picker-popup { position:absolute; top:calc(100% + 8px); left:0; z-index:50; }
        .st-picker-backdrop { position:fixed; inset:0; }
        .st-picker-inner { position:relative; z-index:1; border-radius:14px; overflow:hidden; box-shadow:0 8px 32px rgba(15,23,42,.18); }

        /* Pickers row */
        .st-pickers-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }

        /* Preview */
        .st-preview-section { display:flex; flex-direction:column; gap:8px; }
        .st-preview-label { font-size:11px; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:0.7px; }
        .st-preview-btn { width:100%; padding:11px 20px; border-radius:10px; border:none; font-family:'Plus Jakarta Sans',sans-serif; font-size:14px; font-weight:700; cursor:pointer; transition:background .15s, color .15s; }
        .st-gradient-preview { width:100%; height:44px; border-radius:10px; border:1.5px solid #e2e8f0; }

        /* Footer */
        .st-footer { padding:18px 28px; border-top:1.5px solid #f1f5f9; background:linear-gradient(135deg,#f8faff,#f4f7ff); display:flex; align-items:center; justify-content:space-between; gap:14px; flex-wrap:wrap; }
        .st-footer-hint { display:flex; align-items:center; gap:6px; font-size:12.5px; color:#94a3b8; font-weight:500; }
        .st-save-btn { display:inline-flex; align-items:center; gap:8px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; font-weight:700; color:#fff; padding:11px 24px; border-radius:11px; border:none; background:linear-gradient(135deg,#1d4ed8,#2563eb); cursor:pointer; box-shadow:0 4px 14px rgba(37,99,235,.28); transition:all .15s; }
        .st-save-btn:hover { transform:translateY(-1px); box-shadow:0 8px 24px rgba(37,99,235,.36); }
        .st-save-btn:disabled { opacity:.5; cursor:not-allowed; transform:none; }
        .st-save-spinner { display:inline-block; width:16px; height:16px; border:2px solid rgba(255,255,255,.35); border-top-color:#fff; border-radius:50%; animation:stSpin .65s linear infinite; }

        @media (max-width:900px) { .st-logos-grid { grid-template-columns:1fr 1fr; } .st-colors-grid { grid-template-columns:1fr; } }
        @media (max-width:600px) { .st-logos-grid { grid-template-columns:1fr; } .st-section { padding:20px 18px; } .st-footer { flex-direction:column; align-items:flex-start; } .st-colors-grid { padding:0 18px 18px; } }
      `}</style>
    </div>
  );
};

export default Settings;